/* eslint-disable office-addins/call-sync-before-read */
/* eslint-disable office-addins/call-sync-after-load */
/* eslint-disable office-addins/no-context-sync-in-loop */

import { CellAddress, CoObjectItem, CoObjectSummary, DocumentPartSource } from "../components/interfaces/CoObject";
import { CellRef } from "../components/interfaces/Rule";
import { ElaborationStatusItem } from "../redux/feature/object/remote-elaborations-slice";
import { getPublicResourceAsBase64Binary } from "../utils/axios";
import { CoObjectTypeEnum } from "../api/models";
import {
  addTitleAndTag,
  deleteContentControlById,
  editTitleAndTag,
  getCoObjectFromTagOrCustomXmlByContentControlId,
  getCoObjectFromTagOrCustomXmlByContentControlIdIgnoringDocumentId,
} from "../utils/helpers/ContentControlHelper";
import { downloadDocumentPart } from "../api/document/DocumentsPart";
import { modifyCannotDelete } from "./ContentControlModifiers";
import { ContentControlCreationError, InsertionLocationNotAllowedError } from "./ContentControlErrors";
import type {
  TableSnapshotGrid,
  CellFormat,
  TableCellSnapshot,
  TableTotalsResultModel,
} from "../components/rules/tableTotals/tableTotalsResult.types";
import {
  parseTableTopology,
  buildLogicalGrid,
  buildIdentityGrid,
  type LogicalGrid,
  type PhysicalCellRef,
} from "./TableGridMap";

export { modifyCannotDelete } from "./ContentControlModifiers";

// header/footer type -> OOXML
type HeaderFooterTypeToOOXML = Record<Word.HeaderFooterType, string>;

const getHeaderFooterTypes = (): Word.HeaderFooterType[] => [
  Word.HeaderFooterType.primary,
  Word.HeaderFooterType.firstPage,
  Word.HeaderFooterType.evenPages,
];

const insertHtmlIntoCC = async (
  context: Word.RequestContext,
  cc: Word.ContentControl,
  html: string,
  isWordWeb: boolean
): Promise<void> => {
  if (isWordWeb) {
    cc.insertHtml(html, Word.InsertLocation.replace);
    return;
  }

  cc.clear();
  await context.sync();
  cc.insertHtml(html, Word.InsertLocation.start);
};

const insertFileFromBase64IntoCC = async (
  context: Word.RequestContext,
  cc: Word.ContentControl,
  base64: string,
  isWordWeb: boolean
): Promise<void> => {
  if (isWordWeb) {
    cc.insertFileFromBase64(base64, Word.InsertLocation.replace);
    return;
  }

  cc.clear();
  await context.sync();
  cc.insertFileFromBase64(base64, Word.InsertLocation.start);
};

const insertRefUrlContentIntoCC = async (
  context: Word.RequestContext,
  cc: Word.ContentControl,
  item: ElaborationStatusItem,
  isWordWeb: boolean
): Promise<void> => {
  const refUrl = item.elabResult;
  if (!refUrl) {
    console.warn(`[insertRefUrlIntoCC] REF_URL is null for item ${item.id}, skipping update`);
    return;
  }

  const targetContent = await getPublicResourceAsBase64Binary(refUrl);
  if (!targetContent) {
    console.warn(`[insertRefUrlIntoCC] Could not fetch content from REF_URL for item ${item.id}, skipping update`);
    return;
  }

  await modifyCannotEdit(context, cc, false);
  if (isWordWeb) {
    cc.insertFileFromBase64(targetContent, Word.InsertLocation.replace);
    await context.sync();
    return;
  } else {
    cc.clear();
    cc.insertFileFromBase64(targetContent, Word.InsertLocation.start);
    await context.sync();
    // TODO Header & Footer management is temporarily disabled
    // await insertFilePreservingHeaderAndFooter(context, cc, targetContent);
  }
  await modifyCannotEdit(context, cc, true);
};

const insertInternalRefUrlContentIntoCC = async (
  context: Word.RequestContext,
  cc: Word.ContentControl,
  documentPartId: string,
  isWordWeb: boolean
): Promise<void> => {
  const targetContent = await downloadDocumentPart(documentPartId);
  if (!targetContent) {
    console.warn(
      `[insertInternalRefUrlContentIntoCC] No content for documentPartId ${documentPartId}, skipping update`
    );
    return;
  }

  const unlockedContentControls = await unlockContentControlsForSectionBreaks(context);
  await modifyCannotEdit(context, cc, false);
  if (isWordWeb) {
    cc.insertFileFromBase64(targetContent, Word.InsertLocation.replace);
    await context.sync();
    return;
  } else {
    await insertFilePreservingHeaderAndFooter(context, cc, targetContent);
  }
  await modifyCannotEdit(context, cc, true);
  await relockContentControls(context, unlockedContentControls);
};

const handleParagraphFontSize = async (context: Word.RequestContext, cc: Word.ContentControl): Promise<void> => {
  console.time("handleParagraph");
  const firstParagraph = cc.paragraphs.getFirstOrNullObject();
  const lastParagraph = cc.paragraphs.getLastOrNullObject();
  await context.sync();
  if (!firstParagraph.isNullObject) firstParagraph.font.size = 1;
  if (!lastParagraph.isNullObject) lastParagraph.font.size = 1;
  console.log(`Font size of the first/last paragraph in content control set to 1.`);
  console.timeEnd("handleParagraph");
};

export async function updateContentControlWithElabResult(
  item: ElaborationStatusItem,
  isWordWeb: boolean
): Promise<void> {
  return Word.run(async (context) => {
    const cc = context.document.contentControls.getByIdOrNullObject(Number(item.contentControlId));
    await context.sync();
    if (cc.isNullObject) {
      return Promise.resolve();
    }
    await modifyCannotDelete(context, cc, false);
    if (item.elabResultType === "HTML") {
      if (item.elabResult != null && item.elabResult !== "" && item.elabResult !== undefined) {
        await insertHtmlIntoCC(context, cc, item.elabResult, isWordWeb);
      }
    } else if (item.elabResultType === "STRINGBASE64") {
      if (item.elabResult != null && item.elabResult !== "") {
        await insertFileFromBase64IntoCC(context, cc, item.elabResult, isWordWeb);
        await handleParagraphFontSize(context, cc);
      }
    } else if (item.elabResultType === "IMAGE_STRINGBASE64") {
      const imageResponse = parseImageResponse(item.outputResponse);
      await replaceInlinePicture(cc, item.elabResult, imageResponse?.keepSize);
    } else if (item.elabResultType === "REF_URL") {
      await insertRefUrlContentIntoCC(context, cc, item, isWordWeb);
    } else if (item.elabResultType === "INTERNAL_REF_URL") {
      const documentPartId = item.documentPartIncludedId;
      await insertInternalRefUrlContentIntoCC(context, cc, documentPartId, isWordWeb);
    } else {
      // eslint-disable-next-line office-addins/no-empty-load
      cc.load();
      await context.sync();
      cc.insertText(item.elabResult, Word.InsertLocation.replace);
      cc.cannotEdit = false;
    }
    cc.cannotDelete = true;

    await context.sync();
  });
}

const insertFilePreservingHeaderAndFooter = async (
  context: Word.RequestContext,
  cc: Word.ContentControl,
  content: string
) => {
  if (Office.context.requirements.isSetSupported("WordApiDesktop", "1.3")) {
    const originalSections = cc.getRange().sections;
    originalSections.load("items");
    await context.sync();

    if (!originalSections.items.length) {
      console.warn("No source section found for CC.");
      return;
    }
    const originalSection = originalSections.getFirst();

    // Get OOXML header/footer from root
    const originalHeadersAndFooters = await getHeaderAndFooter(context, originalSection);

    cc.clear();
    cc.insertFileFromBase64(content, Word.InsertLocation.start);
    await context.sync();

    // Apply header/footer
    const updatedSections = cc.getRange().sections;
    await applyHeaderAndFooter(context, updatedSections, originalHeadersAndFooters);
    await context.sync();
  } else {
    cc.clear();
    cc.insertFileFromBase64(content, Word.InsertLocation.start);
    await context.sync();
  }
};

async function getHeaderAndFooter(
  context: Word.RequestContext,
  section: Word.Section
): Promise<{ headers: HeaderFooterTypeToOOXML; footers: HeaderFooterTypeToOOXML }> {
  const types = getHeaderFooterTypes();
  const headerResults = types.map((t) => section.getHeader(t).getOoxml());
  const footerResults = types.map((t) => section.getFooter(t).getOoxml());
  await context.sync();

  const headers = {} as HeaderFooterTypeToOOXML;
  const footers = {} as HeaderFooterTypeToOOXML;

  types.forEach((t, i) => {
    headers[t] = headerResults[i].value ?? "";
    footers[t] = footerResults[i].value ?? "";
  });
  return { headers, footers };
}

async function applyHeaderAndFooter(
  context: Word.RequestContext,
  sections: Word.SectionCollection,
  headersAndFooters: { headers: HeaderFooterTypeToOOXML; footers: HeaderFooterTypeToOOXML }
): Promise<void> {
  sections.load("items");
  await context.sync();

  const types = getHeaderFooterTypes();
  for (const s of sections.items) {
    for (const t of types) {
      const header = s.getHeader(t);
      header.insertOoxml(headersAndFooters.headers[t] || "", Word.InsertLocation.replace);
      (header as any).linkToPrevious = false;

      const footer = s.getFooter(t);
      footer.insertOoxml(headersAndFooters.footers[t] || "", Word.InsertLocation.replace);
      (footer as any).linkToPrevious = false;
    }
  }
}

async function replaceInlinePicture(cc: Word.ContentControl, base64Image: string, keepSize = false) {
  cc.load("inlinePictures");
  // IDS-6163: per risolvere il problema dell'aggiornamento delle immagini se un content control è selezionato,
  // abbiamo dovuto eliminare la selezione e reimpostarla dopo l'aggiornamento di essa.
  // Questo perché se un cc diverso da quello da aggiornare è selezionato va in errore il metodo image.set()
  // chiamato dopo la insert dell'immagine.
  // Nelle due righe sotto si va a:
  // - salvare l'attuale selezione (range) che poi verrà ripristinata
  // - collassare l'attuale selezione all'inizio
  //
  // Nota: document.selection (Word.Selection) è Desktop-only e non disponibile su Word Web.
  // Si usa getRange("Start").select() che è cross-platform (Word API 1.1) e ha lo stesso effetto
  // di collassare la selezione corrente al punto iniziale.
  const selectedObject = cc.context.document.getSelection();
  selectedObject.getRange("Start").select();
  await cc.context.sync();

  let height: number, width: number, lockAspectRatio: boolean;

  if (keepSize) {
    const oldImage = cc.inlinePictures.getFirstOrNullObject();
    await cc.context.sync();

    if (!oldImage.isNullObject) {
      oldImage.load(["height", "width", "lockAspectRatio"]);
      await cc.context.sync();

      height = oldImage.height;
      width = oldImage.width;
      lockAspectRatio = oldImage.lockAspectRatio;
    }
  }

  const newImage = cc.insertInlinePictureFromBase64(base64Image, Word.InsertLocation.replace);

  if (newImage !== undefined) {
    // Unlock aspect ratio before resizing
    newImage.lockAspectRatio = false;

    if (height && width) {
      newImage.set({ height, width });
    }

    // Restore aspect ratio lock
    newImage.lockAspectRatio = lockAspectRatio;
  }

  // IDS-6163: ripristina la selezione salvata prima dell'inserimento dell'immagine.
  selectedObject.select();

  await cc.context.sync();
}

// Best-effort removal of an orphan content control created by createContentControlForCoObject.
// Runs inside the same Word.run, on the still-live proxy/context, and never throws itself: its
// success/failure is reported back as a boolean so the caller can build an accurate error.
async function tryDeleteOrphanContentControl(
  wordContentControl: Word.ContentControl | undefined,
  context: Word.RequestContext
): Promise<boolean> {
  if (!wordContentControl) return false;
  try {
    wordContentControl.cannotDelete = false;
    wordContentControl.delete(false);
    await context.sync();
    return true;
  } catch (cleanupError) {
    console.warn("createContentControlForCoObject: failed to clean up orphan content control", cleanupError);
    return false;
  }
}

export function createContentControlForCoObject(coObject: CoObjectItem, inline: boolean): Promise<Word.ContentControl> {
  return Word.run(async (context) => {
    const range = context.document.getSelection();

    // Resolve the selection's table context BEFORE any mutation, following the same
    // parentTableCellOrNullObject pattern as getSelectedTableCellRange (ContentControl.ts:1011-1035).
    const startRange = range.getRange("Start");
    const endRange = range.getRange("End");
    range.load("parentTableCellOrNullObject");
    startRange.load("parentTableCellOrNullObject");
    endRange.load("parentTableCellOrNullObject");
    const startCell = startRange.parentTableCellOrNullObject;
    const endCell = endRange.parentTableCellOrNullObject;
    startCell.load(["rowIndex", "cellIndex"]);
    endCell.load(["rowIndex", "cellIndex"]);
    await context.sync();

    const cellsDiffer =
      startCell.isNullObject !== endCell.isNullObject ||
      (!startCell.isNullObject &&
        !endCell.isNullObject &&
        (startCell.rowIndex !== endCell.rowIndex || startCell.cellIndex !== endCell.cellIndex));

    if (cellsDiffer) {
      // Nothing was created yet, so throwing here (before range.clear()) prevents silent
      // cross-cell data loss when the selection spans more than one table cell.
      throw new InsertionLocationNotAllowedError();
    }

    const isInsideTableCell = !startCell.isNullObject;

    let wordContentControl: Word.ContentControl | undefined;

    try {
      // Sync 1 (create + identify): clear() is skipped inside a table cell since the selection
      // is collapsed there and clear() is unreliable in that context.
      if (!isInsideTableCell) {
        range.clear();
      }
      wordContentControl = range.insertContentControl();
      wordContentControl.load("id");
      await context.sync();

      const id = wordContentControl.id;
      if (id === undefined || id === null || Number.isNaN(id)) {
        throw new Error("createContentControlForCoObject: inserted content control has no usable id");
      }
    } catch (cause) {
      const orphanCleaned = await tryDeleteOrphanContentControl(wordContentControl, context);
      throw new ContentControlCreationError(cause, undefined, orphanCleaned);
    }

    try {
      // Sync 2 (configure): same three statements, same order as before.
      wordContentControl.cannotDelete = true;
      wordContentControl.title = coObject.info.objectName;
      wordContentControl.cannotEdit = false;
      await context.sync();
    } catch (cause) {
      const orphanCleaned = await tryDeleteOrphanContentControl(wordContentControl, context);
      throw new ContentControlCreationError(cause, wordContentControl.id, orphanCleaned);
    }

    if (!isInsideTableCell && !inline) {
      try {
        // Sync 3 (cosmetic spacers), only outside a table cell: Word rejects the inline-to-block
        // conversion these paragraph inserts require when the content control lives inside a
        // table cell (OfficeDev/office-js#53), so this cosmetic step is skipped there instead.
        // Also skipped for inline objects, which must not be surrounded by extra paragraphs.
        wordContentControl.insertParagraph("", Word.InsertLocation.after);
        wordContentControl.insertParagraph("", Word.InsertLocation.before);
        await context.sync();
      } catch (error) {
        console.warn("createContentControlForCoObject: failed to insert spacer paragraphs", error);
      }
    }

    return wordContentControl;
  });
}

export function enableMoveCC(id: number, cannotDelete: boolean): Promise<any> {
  return Word.run(async (context) => {
    const wordContentControl: Word.ContentControl = context.document.contentControls.getById(id);
    wordContentControl.cannotDelete = cannotDelete;
    await context.sync();
  });
}

export function lockObjectCannotEdit(id: number, cannotEdit: boolean): Promise<void> {
  return Word.run(async (context) => {
    const contentControl: Word.ContentControl = context.document.contentControls.getById(id);
    contentControl.cannotEdit = cannotEdit;
    await context.sync();
  });
}

// modifyCannotDelete moved to ContentControlModifiers.ts

export async function modifyCannotEdit(
  context: Word.RequestContext,
  cc: Word.ContentControl,
  value: boolean
): Promise<void> {
  if (!cc) {
    console.warn("[modifyCannotEdit] Content Control is not available");
    return;
  }

  try {
    // we read the content controls from the range because reading them directly from the cc is not reliable after clear operation
    // reading from the range, include also the current cc
    const rangeContentControls = cc.getRange().contentControls;
    rangeContentControls.load("items/id");
    await context.sync();

    console.debug(`[modifyCannotEdit] Setting cannotEdit=${value} to CC with id=${cc.id}`);
    cc.cannotEdit = value;

    // we filter the current cc, to get only its children
    const innerContentControls = rangeContentControls.items
      .filter((item) => item.id !== cc.id)
      .map((item) => context.document.contentControls.getById(item.id));

    console.debug(`[modifyCannotEdit] CC with id=${cc.id} has ${innerContentControls.length} child`);

    for (const innerCc of innerContentControls) {
      innerCc.cannotEdit = value;
    }
    await context.sync();
  } catch (error) {
    console.error("[modifyCannotEdit] Error modifying cannotEdit:", error);
  }
}

/**
 * Trova tutti i content control nel documento che hanno section breaks
 * e restituisce i loro ID insieme allo stato cannotEdit corrente
 */
async function findContentControlsWithSectionBreaks(
  context: Word.RequestContext
): Promise<Array<{ id: number; wasCannotEdit: boolean }>> {
  const result: Array<{ id: number; wasCannotEdit: boolean }> = [];

  try {
    const allContentControls = context.document.contentControls;
    allContentControls.load("items");
    await context.sync();

    for (const cc of allContentControls.items) {
      cc.load(["id", "cannotEdit"]);
      await context.sync();

      // Controlla se il content control contiene section breaks
      const range = cc.getRange(Word.RangeLocation.whole);
      range.load("text");
      await context.sync();

      // Cerca section breaks nei paragraphs
      const paragraphs = cc.paragraphs;
      paragraphs.load("items");
      await context.sync();

      let hasSectionBreak = false;
      for (const paragraph of paragraphs.items) {
        try {
          paragraph.load("text");
          await context.sync();

          // I section breaks sono spesso alla fine dei paragrafi
          // Prova a verificare se ci sono section breaks
          const range = paragraph.getRange(Word.RangeLocation.whole);
          const nextRange = range.getRange(Word.RangeLocation.after);
          nextRange.load("text");
          await context.sync();

          // Se il testo contiene caratteri speciali o se è un paragrafo vuoto seguito da cambio sezione
          // potrebbe contenere un section break
          if (paragraph.text === "" || paragraph.text.includes("\f")) {
            hasSectionBreak = true;
            break;
          }
        } catch {
          // Ignora errori per paragrafi individuali
        }
      }

      if (hasSectionBreak && cc.cannotEdit) {
        result.push({ id: cc.id, wasCannotEdit: true });
      }
    }
  } catch (error) {
    console.error("Error finding content controls with section breaks:", error);
  }

  return result;
}

/**
 * Sblocca temporaneamente tutti i content control che potrebbero essere
 * influenzati dall'inserimento di section breaks
 */
async function unlockContentControlsForSectionBreaks(
  context: Word.RequestContext
): Promise<Array<{ id: number; wasCannotEdit: boolean }>> {
  const contentControlsToUnlock = await findContentControlsWithSectionBreaks(context);

  for (const ccInfo of contentControlsToUnlock) {
    try {
      const cc = context.document.contentControls.getById(ccInfo.id);
      await modifyCannotEdit(context, cc, false);
      console.log(`Temporarily unlocked content control ${ccInfo.id} for section break operation`);
    } catch (error) {
      console.error(`Error unlocking content control ${ccInfo.id}:`, error);
    }
  }

  await context.sync();
  return contentControlsToUnlock;
}

/**
 * Riblocca i content control che erano stati sbloccati temporaneamente
 */
async function relockContentControls(
  context: Word.RequestContext,
  contentControlsToRelock: Array<{ id: number; wasCannotEdit: boolean }>
): Promise<void> {
  for (const ccInfo of contentControlsToRelock) {
    if (ccInfo.wasCannotEdit) {
      try {
        const cc = context.document.contentControls.getById(ccInfo.id);
        await modifyCannotEdit(context, cc, true);
        console.log(`Re-locked content control ${ccInfo.id} after section break operation`);
      } catch (error) {
        console.error(`Error re-locking content control ${ccInfo.id}:`, error);
      }
    }
  }

  await context.sync();
}

export async function selectContentControl(coObject: CoObjectItem) {
  return selectContentControlById(Number(coObject.contentControlId));
}

export async function selectContentControlById(contentControlId: number) {
  try {
    return await Word.run(async (context) => {
      let contentControl = context.document.contentControls.getById(contentControlId);
      contentControl.select();
      await context.sync();
      return true;
    });
  } catch (error) {
    console.error(error);
    return false;
  }
}

export const selectWordCell = async (contentControlId: number, cellAddress: CellAddress) => {
  try {
    return await Word.run(async (context) => {
      const contentControl = context.document.contentControls.getByIdOrNullObject(contentControlId);
      await context.sync();
      if (contentControl.isNullObject) return false;
      const table = contentControl.getRange().tables.getFirstOrNullObject();
      await context.sync();
      if (table.isNullObject) return false;
      const cell = table.getCellOrNullObject(cellAddress.row, cellAddress.column);
      await context.sync();
      if (cell.isNullObject) return false;
      cell.select();
      return true;
    });
  } catch (error) {
    console.error(error);
    return false;
  }
};

export async function selectCellOrContentControl(coObjectSummary: CoObjectSummary) {
  if (!coObjectSummary.contentControlId) return;
  if (coObjectSummary.column != null && coObjectSummary.row != null) {
    const cellAddress: CellAddress = {
      row: coObjectSummary.row,
      column: coObjectSummary.column,
    };
    return void selectWordCell(coObjectSummary.contentControlId, cellAddress);
  }

  return void selectContentControlById(coObjectSummary.contentControlId);
}

export function getSubPartContentControl(contentControlId: number) {
  return Word.run(async (context) => {
    const contentControl = context.document.contentControls.getById(contentControlId);
    let lastParent: Word.ContentControl;
    try {
      contentControl.load("parentContentControl");
      await context.sync();
      lastParent = contentControl.parentContentControl;
    } catch {
      // the content control is on the root document and
      return false;
    }

    let parent = lastParent;
    while (parent != null) {
      try {
        lastParent.load("id,parentContentControl");

        await context.sync();
        parent = lastParent.parentContentControl;
        lastParent = parent;
      } catch {
        parent = null;
      }
    }

    return await getCoObjectFromTagOrCustomXmlByContentControlId(lastParent.id);
  });
}

/**
 * return true if the content control last parent in the content control hierarchy
 * is a coObject of type DOCUMENT_PART_INCLUDE by default or a different type
 * @param contentControlId
 * @param objectType
 */
export async function isSubPartContentControl(
  contentControlId: number,
  objectType: CoObjectTypeEnum = CoObjectTypeEnum.DOCUMENT_PART_INCLUDE
): Promise<boolean> {
  const lastParentCoObject = await getSubPartContentControl(contentControlId);
  return !!lastParentCoObject && lastParentCoObject?.info?.objectType == objectType;
}

export const parseImageResponse = (json: string): ImageResponse => {
  return JSON.parse(json) as ImageResponse;
};

export const parseDetachResponse = (json: string): DetachResponse => {
  return JSON.parse(json) as DetachResponse;
};

export const parseDuplicateResponse = (json: string): DuplicateResponse => {
  return JSON.parse(json) as DuplicateResponse;
};

export const createCoObjectFromDuplicateResponse = (
  duplicateResponse: DuplicateResponse,
  sharepointPath: string
): Omit<CoObjectItem, "source"> & {
  source: DocumentPartSource;
} => {
  return {
    id: duplicateResponse.id,
    documentId: duplicateResponse.documentId,
    contentControlId: Number(duplicateResponse.contentControlId),
    info: {
      objectName: duplicateResponse.objectName,
      info: duplicateResponse.info,
      objectType: CoObjectTypeEnum.DOCUMENT_PART_INCLUDE,
    },
    source: {
      path: sharepointPath,
      includedDocumentPartId: duplicateResponse.includedDocumentId,
      type: "DOCUMENT_PART_INCLUDE",
    },
  };
};

export const handleDetachResult = async (item: ElaborationStatusItem) => {
  const detachResponse = parseDetachResponse(item.outputResponse);
  const coObject = createCoObjectFromDuplicateResponse(
    detachResponse,
    (
      item as unknown as {
        sharepointPath: string;
      }
    ).sharepointPath
  );

  await addTitleAndTag(coObject.contentControlId, coObject);
  await deleteContentControlById(Number(detachResponse.contentControlIdToBeDetached));
};

export const handleCloneResult = async (item: ElaborationStatusItem) => {
  const duplicateResponse = parseDuplicateResponse(item.outputResponse);
  const coObject = createCoObjectFromDuplicateResponse(
    duplicateResponse,
    (
      item as unknown as {
        sharepointPath: string;
      }
    ).sharepointPath
  );

  await addTitleAndTag(coObject.contentControlId, coObject);
};

export const handleRefreshComponentInstanceResult = async (item: ElaborationStatusItem): Promise<CoObjectItem> => {
  const updatedCoObject = JSON.parse(item.outputResponse) as CoObjectItem;
  editTitleAndTag(updatedCoObject);
  return updatedCoObject;
};

export interface DuplicateResponse {
  id: string;
  documentId: string;
  contentControlId: string;
  objectName: string;
  info: string;
  includedDocumentId: string;
}

export interface DetachResponse extends DuplicateResponse {
  contentControlIdToBeDetached: string;
}

export interface ImageResponse {
  keepSize: boolean;
}

export async function getParentContentControls(): Promise<Word.ContentControl | null> {
  // TODO: this function may be superseded by retrieveNearestParentTagetikContentControlFromSelection in all cases. Need to verify and possibly deprecate/delete it.
  return Word.run(async (context) => {
    try {
      const range = context.document.getSelection();
      context.load(range);
      await context.sync();
      const parentContentControl = range.parentContentControlOrNullObject;
      await context.sync();
      if (parentContentControl.isNullObject) {
        console.warn("No parent content control in selection ", { range });
        return null;
      }
      context.load(parentContentControl, "id");
      await context.sync();

      const contentControl = context.document.contentControls.getById(parentContentControl.id);
      contentControl.load(["cannotEdit", "cannotDelete"]);
      await context.sync();
      return contentControl;
    } catch (err) {
      console.error("ERROR GETTING PARENT CC", err);
      return null;
    }
  });
}

/**
 * Outcome of resolving a single ancestor content control during the upward walk performed by
 * `retrieveNearestParentTagetikContentControlFromSelection`:
 * - `returnContentControl`: STRICT resolution succeeded for a genuine (non-wrapper) Tagetik object;
 *   the walk must stop and return it right away (today's fast path). `contentControl` is `null` when
 *   Word failed to (re)load `cannotEdit`/`cannotDelete` for it -- that failure aborts the whole walk,
 *   exactly like the original code did.
 * - `ownedWrapper`: STRICT resolution succeeded for a DOCUMENT_PART_INCLUDE wrapper -- its documentId
 *   matches the root Document-ID, so anything nested inside it is genuinely owned by this document.
 *   The walk must mark ownership and, if a candidate is already pending, return it.
 * - `pendingCandidate`: STRICT resolution failed, but the RELAXED (ccid-only) resolver matched a
 *   genuine (non-wrapper) Tagetik object and no candidate was pending yet -- hold it, to be trusted
 *   only if an owning DOCUMENT_PART_INCLUDE wrapper is found further up the walk.
 * - `continue`: nothing matched at this ancestor (or a candidate was already pending); keep walking.
 */
type NearestParentAncestorOutcome =
  | { kind: "returnContentControl"; contentControl: Word.ContentControl | null }
  | { kind: "ownedWrapper" }
  | { kind: "pendingCandidate"; contentControl: Word.ContentControl }
  | { kind: "continue" };

/**
 * Decides what `retrieveNearestParentTagetikContentControlFromSelection` should do with a single
 * ancestor content control (`ancestorId`), given whether a `pendingCandidate` is already held from a
 * nearer ancestor (`hasCandidate`). Encapsulates the STRICT/RELAXED resolution and
 * DOCUMENT_PART_INCLUDE ownership decision so the walk itself stays a simple loop (keeps Cognitive
 * Complexity low) -- see `NearestParentAncestorOutcome` for what each outcome means.
 */
async function resolveNearestParentAncestor(
  context: Word.RequestContext,
  selection: Word.Range,
  ancestorId: number,
  depth: number,
  hasCandidate: boolean
): Promise<NearestParentAncestorOutcome> {
  const coObjectItem = await getCoObjectFromTagOrCustomXmlByContentControlId(ancestorId);

  if (coObjectItem && coObjectItem.info.objectType !== CoObjectTypeEnum.DOCUMENT_PART_INCLUDE) {
    try {
      const contentControl = context.document.contentControls.getById(ancestorId);
      contentControl.load(["cannotEdit", "cannotDelete"]);
      await context.sync();
      console.info(`Found parent Tagetik Content Control at depth ${depth} with ID ${ancestorId}.`, { selection });
      return { kind: "returnContentControl", contentControl };
    } catch (err) {
      console.error(
        `Failed to load properties 'cannotEdit' and 'cannotDelete' of Tagetik Content Control with ID ${ancestorId} (depth: ${depth}) from Word's thread or Content Control not found in the document.`,
        err
      );
      return { kind: "returnContentControl", contentControl: null };
    }
  }

  if (coObjectItem?.info.objectType === CoObjectTypeEnum.DOCUMENT_PART_INCLUDE) {
    // Strict resolution succeeded for a DOCUMENT_PART_INCLUDE wrapper: its documentId matches the
    // root Document-ID, so anything nested inside it is genuinely owned by this document.
    console.info(
      `Parent Content Control with ID ${ancestorId} (depth: ${depth}) is a root-owned DOCUMENT_PART_INCLUDE wrapper.`,
      { selection }
    );
    return { kind: "ownedWrapper" };
  }

  // Strict resolution failed for this ancestor (e.g. stale documentId). Retry with the relaxed
  // (ccid-only) resolver: if it is a genuine (non-wrapper) Tagetik object, hold it as a pending
  // candidate -- only trusted later if an owning DOCUMENT_PART_INCLUDE wrapper is found further up.
  // The first (nearest) pending candidate wins; further stale ancestors found afterwards do not
  // override it (guarded here via `hasCandidate`).
  const relaxedCoObjectItem = await getCoObjectFromTagOrCustomXmlByContentControlIdIgnoringDocumentId(ancestorId);
  if (
    relaxedCoObjectItem &&
    relaxedCoObjectItem.info.objectType !== CoObjectTypeEnum.DOCUMENT_PART_INCLUDE &&
    !hasCandidate
  ) {
    try {
      const contentControl = context.document.contentControls.getById(ancestorId);
      contentControl.load(["cannotEdit", "cannotDelete"]);
      await context.sync();
      console.info(
        `Parent Content Control with ID ${ancestorId} (depth: ${depth}) matched only via the relaxed (stale documentId) resolver. Holding as pending candidate while continuing to search for an owning DOCUMENT_PART_INCLUDE wrapper.`,
        { selection }
      );
      return { kind: "pendingCandidate", contentControl };
    } catch (err) {
      console.error(
        `Failed to load properties 'cannotEdit' and 'cannotDelete' of pending candidate Tagetik Content Control with ID ${ancestorId} (depth: ${depth}).`,
        err
      );
      return { kind: "continue" };
    }
  }

  return { kind: "continue" };
}

export async function retrieveNearestParentTagetikContentControlFromSelection() {
  return Word.run(async (context) => {
    try {
      const selection = context.document.getSelection();
      // eslint-disable-next-line office-addins/no-empty-load
      selection.load();
      await context.sync();

      let parentContentControl = selection.parentContentControlOrNullObject;
      await context.sync();

      console.debug("Starting recursive backward traversal to find nearest parent Tagetik Content Control.", {
        selection,
      });
      // `candidate` is a Tagetik Content Control whose STRICT resolution failed (typically a stale
      // `documentId`, e.g. content physically inlined from a DOCUMENT_PART_INCLUDE wrapper after an
      // id-regenerating backend operation such as export/import or duplicate) but whose RELAXED
      // (ccid-only) resolution succeeded. It is only ever trusted (returned) once an ancestor
      // DOCUMENT_PART_INCLUDE wrapper itself passes the STRICT gate (`ownedByRoot`), i.e. that
      // wrapper's own documentId matches the root Document-ID. This preserves the
      // foreign/pasted-content protection the strict gate provides for content with no owning wrapper.
      let candidate: Word.ContentControl | null = null;
      let ownedByRoot = false;
      let depth = 0;
      while (!parentContentControl.isNullObject) {
        parentContentControl.load(["id", "parentContentControlOrNullObject"]);
        await context.sync();

        const outcome = await resolveNearestParentAncestor(
          context,
          selection,
          parentContentControl.id,
          depth,
          candidate != null
        );
        switch (outcome.kind) {
          case "returnContentControl":
            return outcome.contentControl;
          case "ownedWrapper":
            ownedByRoot = true;
            if (candidate) {
              console.info(
                `Ownership confirmed by ancestor DOCUMENT_PART_INCLUDE wrapper: returning pending candidate Tagetik Content Control with ID ${candidate.id}.`,
                { selection }
              );
              return candidate;
            }
            break;
          case "pendingCandidate":
            candidate = outcome.contentControl;
            break;
          case "continue":
            break;
        }

        console.info(
          `Parent Content Control with ID ${parentContentControl.id} (depth: ${depth}) is not a Tagetik Content Control or is of type DOCUMENT_PART_INCLUDE. Moving to its parent...`
        );
        parentContentControl = parentContentControl.parentContentControlOrNullObject;
        await context.sync();
        depth++;
      }

      if (candidate && ownedByRoot) {
        console.info(
          `Pending candidate Tagetik Content Control with ID ${candidate.id} confirmed as owned by the root document after reaching the root (depth ${depth}).`,
          { selection }
        );
        return candidate;
      }

      console.warn(`No parent Tagetik content control found in selection (reached root at depth ${depth}).`, {
        selection,
      });
    } catch (err) {
      console.error(
        "!!! PANIC !!! - Can not retrieve nearest parent Tagetik Content Control from selection. This shouldn't have happened.",
        err
      );
    }
    return null;
  });
}

export async function getContentControls(): Promise<Word.ContentControl[] | null> {
  return Word.run(async (context) => {
    try {
      const range = context.document.getSelection();
      range.load(["items"]);
      await context.sync();
      // eslint-disable-next-line office-addins/load-object-before-read
      const cc = range.contentControls;
      cc.load(["items", "cannotEdit", "cannotDelete"]);
      await context.sync();
      const ccs = cc.items;
      for (let c of ccs) {
        c.load(["cannotEdit", "cannotDelete"]);
      }
      await context.sync();
      return ccs;
    } catch {
      return null;
    }
  });
}

export async function checkIfSelected(): Promise<Word.ContentControl | Word.ContentControl[]> {
  const [parent, ccs] = await Promise.all([
    retrieveNearestParentTagetikContentControlFromSelection(),
    getContentControls(),
  ]);

  if (!(parent || ccs?.length)) {
    return Promise.resolve(selectedEmptyContentControl());
  }
  if (parent && ccs?.length) {
    //if both exist and parent is a freetext I'm selecting a cc inside a freetext
    // IDS-7208 FE-3: `parent` is already an ownership-confirmed CC (from the FE-2 traversal above), so its
    // classification is resolved via the RELAXED resolver -- a stale-but-owned CC must classify the same way
    // as a non-stale one here.
    const coObjectItem = await getCoObjectFromTagOrCustomXmlByContentControlIdIgnoringDocumentId(parent.id);
    if (!coObjectItem || coObjectItem.info.objectType == CoObjectTypeEnum.FREE_TEXT) {
      return Promise.resolve(ccs);
    }
  }
  return Promise.resolve(parent || ccs);
}

export async function isCursorInsideTable() {
  try {
    return await Word.run(async (context) => {
      const selection = context.document.getSelection();
      if (!selection) {
        return false;
      }

      selection.load("parentTable");

      await context.sync();
      return Boolean(selection.parentTable);
    });
  } catch (error) {
    console.error("Errore in isCursorInsideTable:", error);
    return false;
  }
}

export const isAddNewPossible = async () => {
  try {
    const ccs = await checkIfSelected();
    const isCCArray = Array.isArray(ccs);
    if (isCCArray) {
      for (const cc of ccs) {
        // IDS-7208 FE-3: `cc` is already an already-selected/owned CC (from checkIfSelected above), so classify
        // it via the RELAXED resolver -- same reasoning as the single-CC branch below.
        const coObjectItem = await getCoObjectFromTagOrCustomXmlByContentControlIdIgnoringDocumentId(cc.id);
        if (coObjectItem?.info?.objectType != "FREE_TEXT") return false;
      }
      return true;
    }
    // IDS-7208 FE-3: `ccs` here is already an already-selected/owned CC (from checkIfSelected, itself fed by
    // the ownership-confirmed FE-2 traversal), so a stale-but-owned CC must classify the same way as a
    // non-stale one -- route through the RELAXED resolver instead of the STRICT one.
    const coObjectItem = await getCoObjectFromTagOrCustomXmlByContentControlIdIgnoringDocumentId(ccs.id);
    return coObjectItem?.info?.objectType == "FREE_TEXT";
  } catch {
    return true;
  }
};

export async function selectedEmptyContentControl(): Promise<Word.ContentControl | null> {
  return Word.run(async (context) => {
    const sel = context.document.getSelection();
    sel.load("parentContentControlOrNullObject");
    await context.sync();
    let current = sel.parentContentControlOrNullObject;
    current.load(["id", "isNullObject"]);
    await context.sync();
    if (current.isNullObject) {
      return null;
    }
    return current;
  });
}

type SelectedTableCellsResult = {
  contentControlId: number;
  cells: { row: number; column: number; value: string }[];
  selectionKind: "singleCell" | "cellRange" | "wholeContentControl";
};

async function selectionEncompassesContentControl(
  context: Word.RequestContext,
  selection: Word.Range,
  contentControlId: number
): Promise<boolean> {
  try {
    const cc = context.document.contentControls.getById(contentControlId);
    const relationResult = selection.compareLocationWith(cc.getRange("Content"));
    await context.sync();
    return [
      Word.LocationRelation.equal,
      Word.LocationRelation.contains,
      Word.LocationRelation.containsStart,
      Word.LocationRelation.containsEnd,
    ].includes(relationResult.value);
  } catch {
    return false;
  }
}

/**
 * `LocationRelation` values that mean "the selection overlaps this cell's body in some
 * way" — i.e. the cell should be captured. Deliberately excludes `before`, `after`,
 * `adjacentBefore`, `adjacentAfter` and `unrelated`, which mean the cell's body and the
 * selection do not share any content.
 *
 * Built lazily (not as a module-level constant): `Word` is a host global supplied at
 * runtime (or by the test harness), so reading `Word.LocationRelation.*` at module-eval
 * time would run before that global exists.
 */
function overlappingLocationRelations(): ReadonlySet<Word.LocationRelation> {
  return new Set([
    Word.LocationRelation.equal,
    Word.LocationRelation.contains,
    Word.LocationRelation.containsStart,
    Word.LocationRelation.containsEnd,
    Word.LocationRelation.inside,
    Word.LocationRelation.insideStart,
    Word.LocationRelation.insideEnd,
    Word.LocationRelation.overlapsBefore,
    Word.LocationRelation.overlapsAfter,
  ]);
}

/**
 * Returns all table cells covered by the current Word selection that belong to the nearest
 * parent Tagetik table content control. Returns `null` when the selection is not inside a
 * Tagetik table content control, when the selection is outside any table, or when the
 * table's logical grid cannot be resolved (positive disagreement — fail closed).
 *
 * Word `Range`s are linear in document order, so a rectangular cell-block selection (e.g. a
 * whole column dragged across several rows) overlaps every intervening cell too, not just
 * the dragged column — `compareLocationWith` overlap alone can't identify "the selected
 * cells". It is used only to find the selection's overlapping cells (`findOverlappingCells`),
 * whose first and last (document order) become the selection's two corners, which are then
 * bounded into a rectangle in LOGICAL coordinates (`resolveSelectionRectangle`, stable across
 * merges) and resolved to their owning anchors (`collectAnchorsInRectangle`), deduplicating a
 * vertically merged cell to one ref.
 *
 * A collapsed selection (a plain click, no extent) is a special case: Word can report an
 * overlap on a neighboring cell that merely touches the caret's position, which would
 * otherwise widen the two corners into a spurious 1x2 rectangle. When that happens,
 * `resolveCaretCorner` asks Word directly which cell the caret belongs to
 * (`parentTableCellOrNullObject`) and collapses both corners to that one cell instead.
 *
 * All `value` strings are `.trim()`-ed (removes trailing paragraph marks / `\r`).
 */
export async function getSelectedTableCells(): Promise<SelectedTableCellsResult | null> {
  console.time("getSelectedTableCells");
  try {
    const parentCC = await retrieveNearestParentTagetikContentControlFromSelection();
    if (!parentCC) return null;
    const contentControlId = parentCC.id;

    return await Word.run(async (context) => {
      try {
        const selection = context.document.getSelection();
        selection.load(["parentTableOrNullObject", "isEmpty"]);
        await context.sync();

        const table = selection.parentTableOrNullObject;
        if (table.isNullObject) return null;

        const selectsWholeContentControl = await selectionEncompassesContentControl(
          context,
          selection,
          contentControlId
        );

        // A null-object table can make the OOXML request throw synchronously; swallow it here
        // so the row/cell enumeration below still proceeds.
        let ooxmlResult: OfficeExtension.ClientResult<string> | undefined;
        try {
          ooxmlResult = table.getRange("Whole").getOoxml();
        } catch {
          ooxmlResult = undefined;
        }
        table.load("rows/items/cells/items");
        await context.sync();

        // Queue a compareLocationWith + body.text load for EVERY physical cell, addressed by
        // collection ordinal only (never rowIndex/cellIndex — see office-js#3200).
        const cellProxies: Word.TableCell[][] = [];
        const relationResults: OfficeExtension.ClientResult<Word.LocationRelation>[][] = [];
        table.rows.items.forEach((rowProxy) => {
          const rowCellProxies: Word.TableCell[] = [];
          const rowRelationResults: OfficeExtension.ClientResult<Word.LocationRelation>[] = [];
          rowProxy.cells.items.forEach((cellProxy) => {
            rowRelationResults.push(selection.compareLocationWith(cellProxy.body.getRange("Whole")));
            cellProxy.body.load("text");
            rowCellProxies.push(cellProxy);
          });
          cellProxies.push(rowCellProxies);
          relationResults.push(rowRelationResults);
        });
        await context.sync(); // every comparison and body.text resolves here

        const physicalRows = cellProxies.map((row) => row.length);
        const logicalGrid = resolveLogicalGrid(ooxmlResult, physicalRows, "[getSelectedTableCells]");
        if (!logicalGrid) return null; // positive disagreement — fail closed

        const overlappingCells = findOverlappingCells(cellProxies, relationResults);
        if (overlappingCells.length === 0) return null;

        let corners = { first: overlappingCells[0], last: overlappingCells[overlappingCells.length - 1] };
        if (selection.isEmpty && overlappingCells.length > 1) {
          const caretCorner = await resolveCaretCorner(context, selection, cellProxies, overlappingCells);
          if (caretCorner) corners = { first: caretCorner, last: caretCorner };
        }

        const rectangle = resolveSelectionRectangle(logicalGrid, corners);
        if (!rectangle) return null; // positive disagreement — fail closed

        const cells = collectAnchorsInRectangle(logicalGrid, cellProxies, rectangle);
        if (cells.length === 0) return null;

        return {
          contentControlId,
          cells,
          selectionKind: selectsWholeContentControl
            ? "wholeContentControl"
            : cells.length === 1
              ? "singleCell"
              : "cellRange",
        };
      } catch (err) {
        console.error("[getSelectedTableCells]", err);
        return null;
      }
    });
  } finally {
    console.timeEnd("getSelectedTableCells");
  }
}

/** Every physical cell overlapping the selection, in document order (row-major). */
function findOverlappingCells(
  cellProxies: Word.TableCell[][],
  relationResults: OfficeExtension.ClientResult<Word.LocationRelation>[][]
): PhysicalCellRef[] {
  const overlapping = overlappingLocationRelations();
  const cells: PhysicalCellRef[] = [];

  for (let r = 0; r < cellProxies.length; r++) {
    for (let c = 0; c < cellProxies[r].length; c++) {
      if (overlapping.has(relationResults[r][c].value)) cells.push({ physicalRow: r, physicalCell: c });
    }
  }

  return cells;
}

/**
 * Disambiguates a collapsed selection's corner when more than one physical cell reported an
 * overlap: compares each candidate's body against `parentTableCellOrNullObject`'s body and
 * returns the one Word itself reports as `equal`. Returns `null` — degrading to the
 * caller's existing (fail-safe) corners — when the caret has no parent cell, none of the
 * candidates match, or the comparison itself throws.
 */
async function resolveCaretCorner(
  context: Word.RequestContext,
  selection: Word.Range,
  cellProxies: Word.TableCell[][],
  overlappingCells: PhysicalCellRef[]
): Promise<PhysicalCellRef | null> {
  try {
    const caretCell = selection.parentTableCellOrNullObject;
    caretCell.load("isNullObject");
    const caretRange = caretCell.body.getRange("Whole");

    const comparisons = overlappingCells.map((candidate) => ({
      candidate,
      relation: caretRange.compareLocationWith(
        cellProxies[candidate.physicalRow][candidate.physicalCell].body.getRange("Whole")
      ),
    }));
    await context.sync();

    if (caretCell.isNullObject) return null;
    const match = comparisons.find(({ relation }) => relation.value === Word.LocationRelation.equal);
    return match?.candidate ?? null;
  } catch {
    return null;
  }
}

/** A rectangle of logical positions, inclusive on all four bounds. */
type LogicalRectangle = { rowMin: number; rowMax: number; colMin: number; colMax: number };

/**
 * Bounds the two corner physical cells into a rectangle in LOGICAL coordinates — the
 * position each corner itself occupies (`physicalToOccupiedPosition`), never the anchor it
 * may belong to, so a corner on a vertical-merge continuation row bounds the rectangle at
 * its own row rather than growing it up into the anchor's row.
 */
function resolveSelectionRectangle(
  logicalGrid: LogicalGrid,
  corners: { first: PhysicalCellRef; last: PhysicalCellRef }
): LogicalRectangle | null {
  const start = logicalGrid.physicalToOccupiedPosition(corners.first.physicalRow, corners.first.physicalCell);
  const end = logicalGrid.physicalToOccupiedPosition(corners.last.physicalRow, corners.last.physicalCell);
  if (!start || !end) return null;

  return {
    rowMin: Math.min(start.row, end.row),
    rowMax: Math.max(start.row, end.row),
    colMin: Math.min(start.column, end.column),
    colMax: Math.max(start.column, end.column),
  };
}

/**
 * Every logical position in `rectangle`, resolved to its owning anchor and deduplicated —
 * a vertically merged cell selected across several rows maps to the same anchor and is
 * captured exactly once, reading the anchor's own (not the continuation's) body text.
 */
function collectAnchorsInRectangle(
  logicalGrid: LogicalGrid,
  cellProxies: Word.TableCell[][],
  rectangle: LogicalRectangle
): { row: number; column: number; value: string }[] {
  const byAnchor = new Map<string, { row: number; column: number; value: string }>();

  for (let row = rectangle.rowMin; row <= rectangle.rowMax; row++) {
    for (let column = rectangle.colMin; column <= rectangle.colMax; column++) {
      const anchor = logicalGrid.resolveAnchor(row, column);
      if (!anchor) continue;
      const key = `${anchor.row}:${anchor.column}`;
      if (byAnchor.has(key)) continue;

      const anchorPhys = logicalGrid.logicalToPhysical(anchor.row, anchor.column);
      const anchorCell = anchorPhys ? cellProxies[anchorPhys.physicalRow]?.[anchorPhys.physicalCell] : undefined;
      if (!anchorCell) continue;

      byAnchor.set(key, { row: anchor.row, column: anchor.column, value: anchorCell.body.text.trim() });
    }
  }

  return Array.from(byAnchor.values());
}

/**
 * Scrolls the document to bring the content control identified by `contentControlId` into
 * view, then immediately collapses the selection to the start so no content remains
 * highlighted. Returns `true` on success, `false` on failure.
 */
export async function scrollTableIntoView(contentControlId: number): Promise<boolean> {
  try {
    return await Word.run(async (context) => {
      const cc = context.document.contentControls.getById(contentControlId);
      // Select only the collapsed start range: scrolls the CC into view WITHOUT ever
      // selecting the whole table/CC, so the resulting (non-cell) selection is ignored
      // by the table-totals selection handler.
      cc.getRange("Start").select();
      await context.sync();
      return true;
    });
  } catch (error) {
    console.error(error);
    return false;
  }
}

/**
 * Reads the current trimmed text value of each requested `{row, column}` cell in the
 * table content control identified by `contentControlId`.
 *
 * Missing or out-of-range cells produce `value: ""` — this function never throws.
 * The returned array preserves the order of the input `cells` array.
 */
export async function getTableCellValues(
  contentControlId: number,
  cells: CellRef[]
): Promise<Array<{ row: number; column: number; value: string }>> {
  if (!cells.length) return [];
  try {
    return await Word.run(async (context) => {
      const contentControl = context.document.contentControls.getByIdOrNullObject(contentControlId);
      await context.sync();
      if (contentControl.isNullObject) {
        return cells.map((c) => ({ row: c.row, column: c.column, value: "" }));
      }

      const table = contentControl.getRange().tables.getFirstOrNullObject();
      table.load("values");
      // A null-object table can make the OOXML request throw synchronously; swallow it
      // here so `values`/`isNullObject` still resolve and the guard below still applies.
      let ooxmlResult: OfficeExtension.ClientResult<string> | undefined;
      try {
        ooxmlResult = table.getRange("Whole").getOoxml();
      } catch {
        ooxmlResult = undefined;
      }
      await context.sync();
      if (table.isNullObject) {
        return cells.map((c) => ({ row: c.row, column: c.column, value: "" }));
      }

      const values: string[][] = table.values;
      const physicalRows = values.map((r) => r.length);
      const logicalGrid = resolveLogicalGrid(ooxmlResult, physicalRows, "[getTableCellValues]");
      if (!logicalGrid) {
        return cells.map((c) => ({ row: c.row, column: c.column, value: "" }));
      }

      // Translate each logical CellRef to its physical {row, cellOrdinal} up front so only
      // resolvable refs need their body text loaded.
      const physicalRefs = cells.map((ref) => logicalGrid.logicalToPhysical(ref.row, ref.column));

      table.load("rows/items/cells/items/body/text");
      await context.sync(); // body.text is now populated for every physical cell

      return cells.map((ref, index) => {
        const phys = physicalRefs[index];
        if (!phys) return { row: ref.row, column: ref.column, value: "" };
        const cellProxy = table.rows.items[phys.physicalRow]?.cells.items[phys.physicalCell];
        if (!cellProxy) return { row: ref.row, column: ref.column, value: "" };
        return { row: ref.row, column: ref.column, value: cellProxy.body.text.trim() };
      });
    });
  } catch (err) {
    console.error("[getTableCellValues]", err);
    return cells.map((c) => ({ row: c.row, column: c.column, value: "" }));
  }
}

/**
 * Resolves a table's OOXML-derived topology and Office's per-row physical cell counts
 * (`physicalRows`) into a merge-aware `LogicalGrid`.
 *
 * Resolution policy (the hotfix's central regression guard): if the OOXML is unavailable
 * (request threw, or `ooxmlResult` is `undefined`) or unparseable, fall back to a physical
 * identity grid (logs a `console.warn`); if that also fails (a jagged physical table with
 * no merge information), the caller must treat the table as unreadable. If the topology
 * DID parse, project it via `buildLogicalGrid`; a `null` result there is a POSITIVE
 * disagreement between the declared topology and the physical shape Office reports, and
 * the caller must fail closed (never silently fall back) in that case.
 */
function resolveLogicalGrid(
  ooxmlResult: OfficeExtension.ClientResult<string> | undefined,
  physicalRows: number[],
  logPrefix: string
): LogicalGrid | null {
  let ooxml: string | null = null;
  if (ooxmlResult) {
    try {
      ooxml = ooxmlResult.value;
    } catch {
      ooxml = null;
    }
  }

  const topology = ooxml !== null ? parseTableTopology(ooxml) : null;
  if (!topology) {
    console.warn(`${logPrefix} table topology unavailable; falling back to a physical identity grid`);
    return buildIdentityGrid(physicalRows);
  }
  return buildLogicalGrid(topology, physicalRows);
}

/** Returns `null` for `undefined` or empty string, otherwise the original string. */
function emptyToNull(s?: string): string | null {
  return s === undefined || s === "" ? null : s;
}

/** Maps a resolved Word.TableBorder to a CSS border shorthand, or null when there is no border. */
function borderToCss(border: Word.TableBorder): string | null {
  const type = border?.type as unknown as string | undefined;
  if (type === undefined || type === null || type === "None" || type === "Mixed") {
    return null;
  }
  const width = typeof border.width === "number" && border.width > 0 ? border.width : 1;
  // Black border color (#000000) is legitimate — do NOT treat it as transparent (unlike shading).
  const color = border.color !== undefined && border.color !== "" ? border.color : "#000000";
  return `${width}px solid ${color}`;
}

/** Maps the four resolved border proxies of a cell to the CellFormat.borders record. */
function mapCellBorders(p: {
  top: Word.TableBorder;
  bottom: Word.TableBorder;
  left: Word.TableBorder;
  right: Word.TableBorder;
}): NonNullable<CellFormat["borders"]> {
  return {
    top: borderToCss(p.top),
    bottom: borderToCss(p.bottom),
    left: borderToCss(p.left),
    right: borderToCss(p.right),
  };
}

/** Maps a resolved Word table cell to a `CellFormat` record. */
function mapCellFormat(cell: Word.TableCell): CellFormat {
  const alignmentMap: Record<string, CellFormat["horizontalAlignment"]> = {
    Left: "left",
    Centered: "center",
    Right: "right",
    Justified: "justify",
  };
  return {
    bold: cell.body.font.bold ?? null,
    italic: cell.body.font.italic ?? null,
    fontFamily: emptyToNull(cell.body.font.name),
    fontSizePt: cell.body.font.size ?? null,
    fontColor: emptyToNull(cell.body.font.color),
    shadingColor: emptyToNull(cell.shadingColor),
    horizontalAlignment: alignmentMap[cell.horizontalAlignment] ?? null,
  };
}

/**
 * Reads the per-cell visual formatting (font, alignment, shading) and per-side borders of a
 * table's cells. Extracted out of the values read path so that path stays a single fast sync;
 * this runs in an independent, fire-and-forget `Word.run` invoked after the values batch resolves.
 *
 * `table` must already be resolved in `context`. Performs its own `context.sync()`. Never throws:
 * returns `undefined` when the sync or any subsequent read fails.
 */
export async function loadTableFormats(
  table: Word.Table,
  rowCount: number,
  columnCount: number,
  context: Word.RequestContext,
  logicalGrid?: LogicalGrid
): Promise<CellFormat[][] | undefined> {
  try {
    const cellProxies: Word.TableCell[][] = [];
    const borderProxies: Array<
      Array<{ top: Word.TableBorder; bottom: Word.TableBorder; left: Word.TableBorder; right: Word.TableBorder }>
    > = [];
    table.rows.items.forEach((rowProxy) => {
      const rowCellProxies: Word.TableCell[] = [];
      const rowBorderProxies: Array<{
        top: Word.TableBorder;
        bottom: Word.TableBorder;
        left: Word.TableBorder;
        right: Word.TableBorder;
      }> = [];
      rowProxy.cells.items.forEach((cellProxy) => {
        // Load font/alignment/shading on the cell proxies actually read by `mapCellFormat`,
        // not through one deep `table.load("rows/items/cells/items/body/font/...")` path: that
        // form does not populate these, leaving every value undefined so the details dialog
        // rendered borders (loaded per-proxy below, which does work) and nothing else.
        cellProxy.load("horizontalAlignment, shadingColor");
        cellProxy.body.font.load("bold, italic, name, size, color");
        const top = cellProxy.getBorder(Word.BorderLocation.top);
        const bottom = cellProxy.getBorder(Word.BorderLocation.bottom);
        const left = cellProxy.getBorder(Word.BorderLocation.left);
        const right = cellProxy.getBorder(Word.BorderLocation.right);
        top.load("type, width, color");
        bottom.load("type, width, color");
        left.load("type, width, color");
        right.load("type, width, color");
        rowCellProxies.push(cellProxy);
        rowBorderProxies.push({ top, bottom, left, right });
      });
      cellProxies.push(rowCellProxies);
      borderProxies.push(rowBorderProxies);
    });
    await context.sync(); // independent format/border sync, off the values critical path

    const resolvedGrid = logicalGrid ?? buildIdentityGrid(table.values.map((row) => row.length));
    if (!resolvedGrid) {
      return undefined;
    }
    const formatRows: Array<Array<CellFormat | undefined>> = Array.from({ length: rowCount }, () =>
      new Array<CellFormat | undefined>(columnCount).fill(undefined)
    );
    for (let row = 0; row < rowCount; row++) {
      for (let column = 0; column < columnCount; column++) {
        const physical = resolvedGrid.logicalToPhysical(row, column);
        if (!physical) continue;
        const cell = cellProxies[physical.physicalRow]?.[physical.physicalCell];
        if (!cell) continue;
        const base = mapCellFormat(cell);
        const borders = borderProxies[physical.physicalRow]?.[physical.physicalCell];
        formatRows[row][column] = borders ? { ...base, borders: mapCellBorders(borders) } : base;
      }
    }
    return formatRows as CellFormat[][];
  } catch (err) {
    console.error("[loadTableFormats] format capture failed", err);
    return undefined;
  }
}

/**
 * Reads the full value grid (`grid`, `rowCount`, `columnCount`) of the first table inside each
 * content control identified by `contentControlIds`, for all distinct ids, in a single `Word.run`
 * with a single values `context.sync()` — never one `Word.run`/sync per id.
 *
 * Values-only: per-cell formats/borders aren't read here — see `loadTableFormats` for how
 * run-time formatting is attached later, off this values gate.
 *
 * Ids are de-duplicated internally; the returned `Map` has an entry for every id passed in,
 * keyed exactly as passed in. Each entry is `{ found: true, rowCount, columnCount, grid }` on
 * success, or `{ found: false, rowCount: 0, columnCount: 0, grid: [] }` when that id's content
 * control or first table is absent — a problem with one id never affects another.
 *
 * Never throws: if the whole `Word.run` fails, every requested id resolves to `found: false`.
 *
 * The returned `grid` (and `formats`/`spans` when present) is LOGICAL and RECTANGULAR:
 * every row has exactly `columnCount` entries, and `columnCount` comes from the OOXML
 * `tblGrid` declaration, NOT from the widest physical row. A logical position covered by
 * another cell's `colSpan`/`rowSpan` (horizontal or vertical merge) takes `grid` value
 * `""`, `formats` value `undefined`, and `spans[...].covered === true`; an unclaimed
 * `gridBefore`/`gridAfter` gap owned by no anchor is also `covered === true` but is further
 * flagged `spans[...].unclaimed === true` — see `TableSnapshotGrid` for the full contract.
 */
export async function readTableSnapshots(contentControlIds: string[]): Promise<Map<string, TableSnapshotGrid>> {
  const distinctIds = Array.from(new Set(contentControlIds));
  const result = new Map<string, TableSnapshotGrid>();

  try {
    await Word.run(async (context) => {
      const entries = distinctIds.map((id) => {
        const contentControl = context.document.contentControls.getByIdOrNullObject(Number(id));
        const table = contentControl.getRange().tables.getFirstOrNullObject();
        table.load("values, rowCount, rows/items/cells/items");
        let ooxmlResult: OfficeExtension.ClientResult<string> | undefined;
        try {
          ooxmlResult = table.getRange("Whole").getOoxml();
        } catch {
          ooxmlResult = undefined;
        }
        return { id, contentControl, table, ooxmlResult };
      });

      await context.sync(); // single values sync for every distinct id in this batch

      for (const { id, contentControl, table, ooxmlResult } of entries) {
        if (contentControl.isNullObject || table.isNullObject) {
          result.set(id, { found: false, rowCount: 0, columnCount: 0, grid: [] });
          continue;
        }
        const values: string[][] = table.values;
        const logicalGrid = resolveLogicalGrid(
          ooxmlResult,
          values.map((row) => row.length),
          `[readTableSnapshots:${id}]`
        );
        if (!logicalGrid) {
          result.set(id, { found: false, rowCount: 0, columnCount: 0, grid: [] });
          continue;
        }
        const { rowCount, columnCount } = logicalGrid;
        result.set(id, {
          found: true,
          rowCount,
          columnCount,
          grid: Array.from({ length: rowCount }, (_, row) =>
            Array.from({ length: columnCount }, (_, column) => {
              const physical = logicalGrid.logicalToPhysical(row, column);
              return physical ? (values[physical.physicalRow]?.[physical.physicalCell] ?? "") : "";
            })
          ),
          spans: logicalGrid.positions.map((row) =>
            row.map(({ colSpan, rowSpan, covered, anchorRow }) => ({
              colSpan,
              rowSpan,
              covered,
              unclaimed: anchorRow === null,
            }))
          ),
        });
      }
    });
  } catch (err) {
    console.error("[readTableSnapshots]", err);
  }

  // Guarantee an entry for every distinct id, even after a total or partial Word.run failure.
  for (const id of distinctIds) {
    if (!result.has(id)) {
      result.set(id, { found: false, rowCount: 0, columnCount: 0, grid: [] });
    }
  }

  return result;
}

/**
 * Reads the full value grid of the first table inside the content control identified by
 * `contentControlId` in a single `Word.run`/`context.sync()`.
 *
 * Thin wrapper over `readTableSnapshots([contentControlId])`, kept so existing callers are
 * unaffected.
 *
 * Returns `{ found: true, rowCount, columnCount, grid }` on success, or
 * `{ found: false, rowCount: 0, columnCount: 0, grid: [] }` when the content control is
 * absent, contains no table, or any Word error occurs. This function never throws.
 *
 * The returned `grid` (and `formats`/`spans` when present) is LOGICAL and RECTANGULAR:
 * every row has exactly `columnCount` entries, and `columnCount` comes from the OOXML
 * `tblGrid` declaration, NOT from the widest physical row. A logical position covered by
 * another cell's `colSpan`/`rowSpan` (horizontal or vertical merge) takes `grid` value
 * `""`, `formats` value `undefined`, and `spans[...].covered === true`; an unclaimed
 * `gridBefore`/`gridAfter` gap owned by no anchor is also `covered === true` but is further
 * flagged `spans[...].unclaimed === true` — see `TableSnapshotGrid` for the full contract.
 */
export async function readTableSnapshot(contentControlId: string): Promise<TableSnapshotGrid> {
  console.time("readTableSnapshot");
  try {
    const map = await readTableSnapshots([contentControlId]);
    return map.get(contentControlId) ?? { found: false, rowCount: 0, columnCount: 0, grid: [] };
  } finally {
    console.timeEnd("readTableSnapshot");
  }
}

/**
 * Reads per-cell run-time formats/borders for the first table inside each content control
 * identified by `contentControlIds`, in its own, separate, independent second `Word.run` — never
 * the one `readTableSnapshots` uses for values, and never held open awaiting it.
 *
 * Self-contained: for each distinct incoming id it re-resolves the content control + first table
 * and re-derives `rowCount`/`columnCount` from its own load, then delegates per-cell format
 * capture to `loadTableFormats`.
 *
 * Never throws: a failure of the whole `Word.run`, or of any single id's resolution, yields
 * `undefined` formats for the affected id(s) only — a format failure degrades that table's
 * fidelity (see `attachTableFormats`) without affecting the already-built result row.
 *
 * Returns a `Map` keyed exactly as passed in, with an entry for every distinct incoming id
 * (`CellFormat[][]` on success, `undefined` on failure or a missing table).
 */
export async function readTableFormats(contentControlIds: string[]): Promise<Map<string, CellFormat[][] | undefined>> {
  const distinctIds = Array.from(new Set(contentControlIds));
  const result = new Map<string, CellFormat[][] | undefined>();

  try {
    await Word.run(async (context) => {
      const entries = distinctIds.map((id) => {
        const contentControl = context.document.contentControls.getByIdOrNullObject(Number(id));
        const table = contentControl.getRange().tables.getFirstOrNullObject();
        table.load("values, rowCount, rows/items/cells/items");
        let ooxmlResult: OfficeExtension.ClientResult<string> | undefined;
        try {
          ooxmlResult = table.getRange("Whole").getOoxml();
        } catch {
          ooxmlResult = undefined;
        }
        return { id, contentControl, table, ooxmlResult };
      });

      await context.sync(); // resolves this run's own contentControl/table + rowCount/columnCount

      for (const { id, contentControl, table, ooxmlResult } of entries) {
        if (contentControl.isNullObject || table.isNullObject) {
          result.set(id, undefined);
          continue;
        }
        const values: string[][] = table.values;
        const logicalGrid = resolveLogicalGrid(
          ooxmlResult,
          values.map((row) => row.length),
          `[readTableFormats:${id}]`
        );
        if (!logicalGrid) {
          result.set(id, undefined);
          continue;
        }
        const { rowCount, columnCount } = logicalGrid;
        // loadTableFormats owns its own context.sync() and never throws.
        result.set(id, await loadTableFormats(table, rowCount, columnCount, context, logicalGrid));
      }
    });
  } catch (err) {
    console.error("[readTableFormats]", err);
  }

  // Guarantee an entry for every distinct id, even after a total or partial Word.run failure.
  for (const id of distinctIds) {
    if (!result.has(id)) {
      result.set(id, undefined);
    }
  }

  return result;
}

/**
 * Attaches previously captured run-time cell formats onto an already-built, stored
 * `TableTotalsResultModel`, setting `TableCellSnapshot.format` on every cell of its `snapshot`
 * grid plus its `addends` and `subtotal` cells.
 *
 * `readTableFormats` captures formats in a background `Word.run` that resolves after the result
 * model has already been built from the values-only snapshot (where every cell's `.format` is
 * `undefined`). This is the "attach" step that reattaches that later-arriving formatting onto the
 * same stored model, restoring run-time fidelity for the details dialog.
 *
 * No-op when `formats` is `undefined` (capture failed or hasn't completed yet) — the model is
 * returned unchanged. Cells outside the captured `formats` grid are left with `format: undefined`.
 *
 * Mutates `model`'s `TableCellSnapshot` objects in place and also returns `model`, for convenient
 * chaining at the call site.
 */
export function attachTableFormats(
  model: TableTotalsResultModel,
  formats: CellFormat[][] | undefined
): TableTotalsResultModel {
  if (!formats) {
    return model;
  }

  const attachToCell = (cell: TableCellSnapshot): void => {
    cell.format = formats[cell.row]?.[cell.column];
  };

  model.snapshot.forEach((row) => row.forEach(attachToCell));
  model.addends.forEach(attachToCell);
  if (model.subtotal) {
    attachToCell(model.subtotal);
  }

  return model;
}
